import type { CatalogModel, CatalogResponse } from '@nadoutong/contracts';
import { PlatformApiError } from '../api/client.ts';
import { useCatalog } from '../api/useCatalog.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { formatUsdRate } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';

interface MatrixChannel {
  readonly slug: string;
  readonly displayName: string;
  readonly rateMultiplier: number;
}

interface VendorMatrix {
  readonly vendor: string;
  readonly displayName: string;
  readonly channels: readonly MatrixChannel[];
  readonly models: readonly CatalogModel[];
}

function compareChannelsByPrice(a: MatrixChannel, b: MatrixChannel): number {
  const priceDelta = a.rateMultiplier - b.rateMultiplier;
  if (priceDelta !== 0) return priceDelta;
  return a.displayName.localeCompare(b.displayName) || a.slug.localeCompare(b.slug);
}

export function Pricing(): JSX.Element {
  const t = useT();
  const catalogQ = useCatalog();
  const matrices = buildVendorMatrices(catalogQ.data);

  return (
    <section data-testid="pricing-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('pricing.title')}
        </h2>
        <p className="text-sm text-outline">{t('pricing.subtitle')}</p>
      </header>

      {catalogQ.isLoading ? (
        <div className="flex flex-col gap-4" data-testid="pricing-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
          <span className="sr-only">{t('pricing.loading')}</span>
        </div>
      ) : catalogQ.isError ? (
        <p className="text-sm text-error" role="alert" data-testid="pricing-error">
          {catalogQ.error instanceof PlatformApiError
            ? `${catalogQ.error.code}: ${catalogQ.error.message}`
            : t('pricing.error')}
        </p>
      ) : matrices.length === 0 ? (
        <p className="text-sm text-outline" data-testid="pricing-empty">
          {t('pricing.empty')}
        </p>
      ) : (
        matrices.map((matrix) => <VendorPricingCard key={matrix.vendor} matrix={matrix} />)
      )}
    </section>
  );
}

function VendorPricingCard({ matrix }: { readonly matrix: VendorMatrix }): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid={`pricing-vendor-${matrix.vendor}`}
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {matrix.displayName}
          </h3>
          <p className="text-xs text-outline">
            {t('pricing.vendor.summary', {
              models: matrix.models.length,
              channels: matrix.channels.length,
            })}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('pricing.unit')}
        </span>
      </header>

      {matrix.channels.length === 0 ? (
        <p className="text-sm text-outline" data-testid={`pricing-vendor-${matrix.vendor}-empty`}>
          {t('pricing.vendor.no_channels')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
                <th className="sticky left-0 z-[1] bg-surface-container px-3 py-2 font-semibold">
                  {t('pricing.column.model')}
                </th>
                {matrix.channels.map((channel) => (
                  <th key={channel.slug} className="px-3 py-2 font-semibold">
                    <span className="flex flex-col gap-1">
                      <span>{channel.displayName}</span>
                      <span className="font-mono text-[10px] font-normal normal-case tracking-normal text-outline">
                        ×{channel.rateMultiplier.toFixed(2)}
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.models.map((model) => (
                <tr key={model.model} className="odd:bg-surface-container-low">
                  <th className="sticky left-0 z-[1] bg-inherit px-3 py-3 text-left align-top font-mono font-semibold text-on-surface">
                    <span className="flex max-w-[240px] flex-col gap-1">
                      <span className="break-all">{model.model}</span>
                      {model.display_name !== null ? (
                        <span className="font-sans text-xs font-normal text-outline">
                          {model.display_name}
                        </span>
                      ) : null}
                    </span>
                  </th>
                  {matrix.channels.map((channel) => (
                    <td
                      key={`${model.model}::${channel.slug}`}
                      className="px-3 py-3 align-top"
                      data-testid={`pricing-cell-${matrix.vendor}-${model.model}-${channel.slug}`}
                    >
                      <PriceCell model={model} channel={channel} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function PriceCell({
  model,
  channel,
}: {
  readonly model: CatalogModel;
  readonly channel: MatrixChannel;
}): JSX.Element {
  const t = useT();
  const covered = model.channels.some((c) => c.slug === channel.slug);
  if (!covered) {
    return <span className="font-mono text-xs text-outline">{t('pricing.cell.unavailable')}</span>;
  }
  return (
    <dl className="grid min-w-[150px] gap-1 font-mono text-xs">
      <PriceLine
        label={t('pricing.rate.input')}
        value={model.input_per_mtok_usd}
        channel={channel}
      />
      <PriceLine
        label={t('pricing.rate.output')}
        value={model.output_per_mtok_usd}
        channel={channel}
      />
      <PriceLine
        label={t('pricing.rate.cache_read')}
        value={model.cache_read_per_mtok_usd}
        channel={channel}
      />
      <PriceLine
        label={t('pricing.rate.cache_write')}
        value={model.cache_write_per_mtok_usd}
        channel={channel}
      />
    </dl>
  );
}

function PriceLine({
  label,
  value,
  channel,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly channel: MatrixChannel;
}): JSX.Element {
  const display = value === null ? '—' : formatUsdRate(value * channel.rateMultiplier);
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-outline">{label}</dt>
      <dd className="text-on-surface">{display}</dd>
    </div>
  );
}

function buildVendorMatrices(catalog: CatalogResponse | undefined): readonly VendorMatrix[] {
  if (catalog === undefined) return [];
  const vendorNames = new Map(catalog.vendors.map((v) => [v.slug, v.display_name]));
  const vendors = new Set<string>();
  for (const vendor of catalog.vendors) vendors.add(vendor.slug);
  for (const model of catalog.models) vendors.add(model.vendor);

  return Array.from(vendors)
    .sort((a, b) => a.localeCompare(b))
    .map((vendor) => {
      const models = catalog.models
        .filter((m) => m.vendor === vendor)
        .sort((a, b) => a.model.localeCompare(b.model));
      const channels = new Map<string, MatrixChannel>();
      for (const model of models) {
        for (const channel of model.channels) {
          if (!channels.has(channel.slug)) {
            channels.set(channel.slug, {
              slug: channel.slug,
              displayName: channel.display_name,
              rateMultiplier: channel.rate_multiplier,
            });
          }
        }
      }
      return {
        vendor,
        displayName: vendorNames.get(vendor) ?? vendor,
        channels: Array.from(channels.values()).sort(compareChannelsByPrice),
        models,
      };
    })
    .filter((m) => m.models.length > 0);
}
